/**
 * SW2-2459 (PR 2) — removing a download once its work is on the server.
 *
 * After the user's own signature on the first inspection line has gone through and nothing of the
 * project is left on the device to upload, the user is asked: remove the download now, or keep it
 * and have it cleared automatically after 24 hours. The automatic clear never removes a project
 * with work waiting to sync; the 3-day cleanup (db.ts `deleteOldOfflineProjects`) stays the outer
 * limit.
 */
import db, { countUnsyncedWork, removeDownloadIfSynced } from '../db/db';
import offlineApi from './api/offline';
import { reviewHoldsReport } from './offlineSigning';

export const AUTO_CLEAR_HOURS = 24;

/**
 * Remove the download from this device and the server's list — only if nothing is waiting. The
 * check and the delete share one transaction (SW2-2475 `removeDownloadIfSynced`): another tab may
 * queue work between them otherwise.
 */
export async function removeDownload(projectId, stillWanted) {
  const removed = await removeDownloadIfSynced(projectId, stillWanted);

  if (removed) {
    await offlineApi.delete(projectId).catch((err) => {
      // Already gone (an admin cleared it) is fine; anything else is worth a trace.
      if (err?.status !== 404 && !/not found/i.test(err?.message || '')) {
        // eslint-disable-next-line no-console
        console.error(`Removed project ${projectId} from this device, but not from the server's download list`, err);
      }
    });
  }

  return removed;
}

/**
 * Clear the download automatically after `hours`. A download from before `downloadedBy` is claimed
 * by the user who agreed to it: auto-clear removes only the user's own (PR #156 review).
 */
export const scheduleClear = (projectId, hours = AUTO_CLEAR_HOURS) =>
  db.transaction('rw', db.projects, db.user, async () => {
    const project = await db.projects.get(projectId);
    const userId = (await db.user.get(1))?.user_id;

    if (!project) return;

    await db.projects.update(projectId, {
      clearAfter: new Date(Date.now() + hours * 3600000).toISOString(),
      ...(project.downloadedBy == null && userId != null ? { downloadedBy: userId } : {}),
    });
  });

/**
 * Whether to ask: the project is downloaded on this device by this user, fully synced, and hasn't
 * been asked about yet (an answer, or the default, sets `clearAfter`).
 */
export async function shouldOfferRemoval(projectId) {
  const project = await db.projects.get(projectId);
  const userId = (await db.user.get(1))?.user_id;

  // No stored user: the automatic clear (own downloads only) could never keep the promise (PR #156).
  return Boolean(
    project &&
      userId != null &&
      !project.clearAfter &&
      (project.downloadedBy == null || project.downloadedBy === userId) &&
      (await countUnsyncedWork(projectId)) === 0,
  );
}

/** Remove this user's downloads whose 24 hours are up and that have nothing waiting. Never throws. */
export async function clearExpiredDownloads(userId) {
  if (navigator.onLine === false) return;

  // Only this user's: a download with no owner was never agreed to by anyone here.
  const isDue = (p) => Boolean(p.clearAfter) && Date.parse(p.clearAfter) <= Date.now() && p.downloadedBy === userId;

  try {
    const due = (await db.projects.toArray()).filter(isDue);

    for (const project of due) {
      // Checked again as it goes: meanwhile another tab may have downloaded it afresh (a new
      // owner, no clear time) or another user signed in (PR #156 review).
      await removeDownload(project.id, isDue).catch((err) => {
        // eslint-disable-next-line no-console
        console.error(`Clearing download ${project.id} failed`, err);
      });
    }
  } catch (err) {
    // eslint-disable-next-line no-console
    console.error('Clearing expired downloads failed', err);
  }
}

/** Is this the first line of the inspection group, per the certification on the device? */
export async function isFirstInspectionLine(inspectionId, lineType) {
  const certification = await db.certifications.get(Number(inspectionId)).catch(() => undefined);

  return certification?.signatureGroups?.inspection?.[0]?.type === lineType;
}

/**
 * SW2-2459 × SW2-2248 — whether signatures the sync just sent leave an inspection waiting on a
 * review, so the download is kept: the reviewer can send the inspection back to be edited.
 *
 * The server's `reviewRaised` when it said so (backend #479). Otherwise the review state saved with
 * the download — the backend before #479 answers with an empty body. That copy can be out of date
 * (the form's setting changed since the download); erring towards keeping is safe, as the 3-day
 * cleanup still removes it. For the same reason a failed read keeps it (PR #173, Copilot): the
 * review state is unknown then, not absent. A missing row is no review.
 */
export async function reviewKeepsDownload(sentSignatures = []) {
  for (const sig of sentSignatures) {
    if (sig.reviewRaised) return true;

    let certification;

    try {
      certification = await db.certifications.get(Number(sig.inspectionId));
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error(`Could not read the review state of inspection ${sig.inspectionId}; keeping the download`, err);

      return true;
    }

    if (reviewHoldsReport(certification?.review)) return true;
  }

  return false;
}
